import { z } from 'zod';
import { todayIso } from './format';
import { dateSchema, optionalDate, requiredText } from './fields';
import { defineEnum } from './enums';
import { idSchema } from './ids';

/** What a workspace API token may do (FR-WS-009): `read` — GET only; `write` — everything the role allows. */
export const ApiTokenScopes = defineEnum('enum.apiTokenScope', [
  ['read', 'info'],
  ['write', 'warning'],
] as const);
export type ApiTokenScope = (typeof ApiTokenScopes.codes)[number];

/** Every token starts with this, so a leaked one is recognisable (secret scanners, logs). */
export const API_TOKEN_PREFIX = 'pmai_';

/** `Authorization: Bearer <token>` → the token, or `null` for any other header. */
export function bearerToken(header: string | undefined): string | null {
  const match = /^Bearer\s+(\S+)\s*$/i.exec(header ?? '');
  return match?.[1] ?? null;
}

/** FR-WS-009: name, scope and an optional last day of validity (in the future). */
export const apiTokenInputSchema = z
  .object({
    name: requiredText(100, 'Nosaukums ir obligāts'),
    scope: ApiTokenScopes.schema,
    expiresOn: optionalDate,
  })
  .refine((v) => !v.expiresOn || v.expiresOn >= todayIso(), {
    path: ['expiresOn'],
    message: 'Termiņš nevar būt pagātnē.',
  });
export type ApiTokenInput = z.input<typeof apiTokenInputSchema>;

export const apiTokenSchema = z.object({
  id: idSchema,
  name: z.string(),
  scope: ApiTokenScopes.schema,
  /** The first characters of the token — to tell tokens apart; the rest is never shown again. */
  hint: z.string(),
  /** Last day the token works (inclusive); `null` — no end. */
  expiresOn: dateSchema.nullable(),
  expired: z.boolean(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type ApiToken = z.infer<typeof apiTokenSchema>;

/** The answer to "create": the only time the token itself is shown (FR-WS-009). */
export const apiTokenCreatedSchema = z.object({ token: z.string(), apiToken: apiTokenSchema });
export type ApiTokenCreated = z.infer<typeof apiTokenCreatedSchema>;
